import { useState } from 'react';
import { ArrowRight, BookOpen, Mic, Heart, Sparkles, Loader2, Star, Quote, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ContactFormModal } from '../components/ContactFormModal';
import { useSiteContent } from '../contexts/SiteContentContext';
import { SEOHead, BookStructuredData } from '../components/SEOHead';

import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

export const PersonalPage = () => {
  const pageRef = useScrollReveal();
  const { get, testimonials } = useSiteContent();
  const [modal, setModal] = useState({ open: false, type: 'personal_dev' });
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const openForm = (type) => setModal({ open: true, type });
  const closeForm = () => setModal({ open: false, type: 'personal_dev' });

  const hero = get('personal_hero');
  const book = get('book_section');
  const personalTestimonials = testimonials('personal');

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      const response = await axios.post(`${API}/waitlist`, { email, source: 'new_book' });
      setSubmitSuccess(true);
      setSubmitMessage(response.data.message);
      setEmail('');
    } catch {
      setSubmitSuccess(false);
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const transformations = [
    { icon: Heart, title: 'Mindset Shift', description: 'Transform limiting beliefs into empowering ones' },
    { icon: Sparkles, title: 'Self-Awareness', description: 'Discover your true strengths and values' },
    { icon: Mic, title: 'Leadership', description: 'Step into your role as a confident leader' },
    { icon: BookOpen, title: 'Growth', description: 'Unlock your full potential' },
  ];

  const bookLink = book.stripe_link;

  return (
    <main className="pt-24" ref={pageRef}>
      <SEOHead path="/personal" />
      <BookStructuredData />
      <ContactFormModal isOpen={modal.open} onClose={closeForm} inquiryType={modal.type} sourcePage="personal" />

      {/* Hero Section */}
      <section className="section-padding bg-[#FDF5F0]" data-testid="personal-hero">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="reveal badge mb-6">{hero.badge_text}</span>
              <h1 className="reveal reveal-delay-1 heading-xl mb-6">{hero.heading_line1}<span className="block text-highlight">{hero.heading_line2}</span></h1>
              <p className="reveal reveal-delay-2 body-text max-w-lg mb-8">{hero.description}</p>
              <button onClick={() => openForm('personal_dev')} className="reveal reveal-delay-3 btn-primary arrow-hover" data-testid="personal-hero-cta">Start Your Journey <ArrowRight className="w-4 h-4" /></button>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-[#E84993]/30 rounded-full blur-2xl" />
              <div className="reveal-image image-frame-accent aspect-[3/4] max-w-md mx-auto lg:max-w-none rounded-3xl">
                <img src="/images/Highlights/Highlights/MMCP5000.jpg" alt="Personal development" className="w-full h-full object-cover object-[center_15%]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Section */}
      <section className="section-padding bg-white" data-testid="personal-transformation">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="reveal overline-text mb-4 block">The Journey</span>
            <h2 className="reveal reveal-delay-1 heading-lg">Transform From Within</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {transformations.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className={`reveal ${index > 0 ? `reveal-delay-${index}` : ''} text-center p-8 rounded-2xl bg-[#FBF8F4] border border-[#F0E6F6] hover:border-[#E84993] transition-colors duration-300`} data-testid={`transformation-${index}`}>
                  <div className="w-14 h-14 bg-[#E84993] rounded-full flex items-center justify-center mx-auto mb-6"><Icon className="w-7 h-7 text-white" /></div>
                  <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                  <p className="body-small">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Personal Testimonials */}
      <section className="py-16 md:py-20 bg-white" data-testid="personal-testimonials">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="reveal overline-text mb-3 block">Transformations</span>
              <h2 className="reveal reveal-delay-1 heading-lg">Stories of Growth</h2>
            </div>
            {personalTestimonials.length > 0 && (
              <div className="reveal reveal-delay-2 bg-[#FDF5F0] p-8 md:p-10 rounded-3xl relative text-center">
                <Quote className="w-10 h-10 text-[#E84993]/30 mx-auto mb-6" />
                <p className="text-lg md:text-xl text-[#4A4A4A] leading-relaxed mb-6 italic" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  "{personalTestimonials[0].quote}"
                </p>
                <div className="flex gap-1 justify-center mb-4">{[...Array(personalTestimonials[0].rating || 5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#E84993] text-[#E84993]" />)}</div>
                <p className="font-medium text-[#1A1A1A]">{personalTestimonials[0].name}</p>
                <p className="text-sm text-[#7A7A7A]">{personalTestimonials[0].role}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Book Section */}
      <section id="book" className="section-padding bg-layered" data-testid="personal-book">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="reveal overline-text mb-4 block">Featured Book</span>
              <h2 className="reveal reveal-delay-1 heading-lg mb-6">{book.heading}</h2>
              <div className="reveal reveal-delay-1 divider-accent mb-6" />
              <p className="reveal reveal-delay-2 body-text mb-6">{book.description1}</p>
              <p className="reveal reveal-delay-2 body-text mb-8">{book.description2}</p>
              {bookLink ? (
                <a href={bookLink} target="_blank" rel="noopener noreferrer" className="reveal reveal-delay-3 btn-primary arrow-hover" data-testid="book-order-cta">
                  Order Your Copy — ${book.price || '25.95'} <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <button onClick={() => openForm('personal_dev')} className="reveal reveal-delay-3 btn-primary arrow-hover" data-testid="book-order-cta">
                  Inquire About the Book <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="reveal-image relative">
                <div className="absolute -bottom-4 -right-4 w-full h-full bg-[#E84993]/20 rounded-2xl" />
                <div className="relative w-64 aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl">
                  <img src="/images/SalonTeam/Website/PERSONAL_BOY/Book/Natasha-Simone_-Images-1-e1764782647476.png" alt="The Beauty of You Book" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* New Book — Dear Beautiful Girl */}
      <section className="section-padding bg-white" data-testid="personal-new-book">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="reveal-image flex justify-center">
              <img
                src="/images/dear-beautiful-girl-mockup-1.jpg"
                alt="Dear Beautiful Girl by Natasha Simone — A Guide to Confidence, Purpose, and Self-Love book cover"
                className="w-full max-w-sm rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <span className="reveal badge mb-6">Coming Soon</span>
              <h2 className="reveal reveal-delay-1 heading-lg mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Dear Beautiful Girl</h2>
              <p className="reveal reveal-delay-1 text-base md:text-lg text-[#B565CC] font-medium mb-2">A Guide to Confidence, Purpose, and Self-Love</p>
              <p className="reveal reveal-delay-2 text-sm italic text-[#9A7FB8] mb-6">"Because the girl you are becoming deserves to be celebrated."</p>
              <p className="reveal reveal-delay-2 body-text mb-8">Written for girls and young women ready to step into their greatness, <em>Dear Beautiful Girl</em> is a heartfelt guide filled with uplifting wisdom, practical exercises, and personal insights to help you build unshakeable confidence, discover your purpose, and embrace self-love — one chapter at a time.</p>
              <form onSubmit={handleWaitlistSubmit} className="reveal reveal-delay-3 max-w-md" data-testid="waitlist-form">
                <p className="text-sm text-[#7A7A7A] mb-3">Be the first to know when it launches:</p>
                <div className="flex gap-3">
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" required className="flex-1 px-5 py-3.5 bg-[#FBF8F4] border border-[#F0E6F6] rounded-full focus:outline-none focus:border-[#E84993] transition-colors duration-300" data-testid="waitlist-email-input" />
                  <button type="submit" disabled={isSubmitting} className="px-6 py-3.5 text-white text-sm font-medium rounded-full transition-colors duration-300 disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #E84993, #D13A7A)' }} data-testid="waitlist-submit-btn">
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Join'}
                  </button>
                </div>
                {submitMessage && <p className={`mt-4 text-sm ${submitSuccess ? 'text-green-600' : 'text-red-600'}`} data-testid="waitlist-message">{submitMessage}</p>}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Speaking Section */}
      <section className="section-padding bg-[#FDF5F0]" data-testid="personal-speaking">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="reveal overline-text mb-4 block">Speaking & Workshops</span>
              <h2 className="reveal reveal-delay-1 heading-lg mb-6">Bringing Transformation to Audiences</h2>
              <p className="reveal reveal-delay-2 body-text mb-6">From school workshops to keynote stages, Natasha delivers powerful sessions on confidence, leadership, and personal growth.</p>
              <ul className="space-y-4 mb-8">
                {['School workshops for students', 'Corporate leadership sessions', 'Keynote speaking engagements'].map((item, i) => (
                  <li key={i} className={`reveal reveal-delay-${i + 2} flex items-center gap-3`}>
                    <div className="w-2 h-2 bg-[#E84993] rounded-full" /><span className="body-text">{item}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => openForm('speaking')} className="reveal reveal-delay-4 btn-outline arrow-hover" data-testid="speaking-cta">Invite Natasha to Speak <ArrowRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {['/images/Events2/EVENT 2/224A2232.jpg', '/images/Events/IMG_7714.JPG', '/images/Events/IMG_6395.JPG', '/images/Events/IMG_6389.JPG'].map((src, i) => (
                <div key={i} className={`reveal-image ${i > 0 ? `reveal-delay-${i}` : ''} aspect-square image-frame img-zoom rounded-2xl`}>
                  <img src={src} alt="Event" className={`w-full h-full object-cover ${i < 2 ? 'object-[center_30%]' : 'object-[center_25%]'}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Global Protocol & International Presence */}
      <section className="section-padding bg-cream" data-testid="avenues-section">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <div className="reveal bg-white rounded-3xl overflow-hidden border border-[#F0E6F6] hover:border-[#B565CC]/20 transition-all duration-500 hover:shadow-xl hover:shadow-[#B565CC]/5">
              <div className="grid grid-cols-1 lg:grid-cols-5">
                <div className="lg:col-span-2 relative overflow-hidden">
                  <div className="aspect-[4/3] lg:aspect-auto lg:h-full">
                    <img src="/images/natasha-speaking-gala.jpg" alt="Natasha Simone — Global Protocol & International Presence" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3A1F3D]/50 to-transparent lg:bg-gradient-to-r" />
                  </div>
                </div>
                <div className="lg:col-span-3 p-8 md:p-10 lg:p-12 flex flex-col justify-center">
                  <span className="overline-text mb-3 block">Executive Presence</span>
                  <h2 className="text-2xl md:text-3xl font-medium mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Global Protocol &<br />International Presence
                  </h2>
                  <div className="divider-accent mb-5" />
                  <p className="body-text mb-4">
                    A premium experience focused on confidence, executive presence, communication, etiquette, and personal transformation for adults seeking to lead with grace and impact.
                  </p>
                  <p className="text-sm text-[#9A9A9A] italic mb-8">
                    Natasha Simone has actively contributed to these transformational experiences through mentorship, leadership conversations, and empowerment initiatives.
                  </p>
                  <a href="https://www.avenuesofexcellence.com/" target="_blank" rel="noopener noreferrer" className="btn-secondary arrow-hover self-start" data-testid="avenues-personal-cta">
                    Explore Avenues of Excellence <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #B565CC 0%, #9B4FBF 50%, #E84993 100%)' }} data-testid="personal-final-cta">
        <div className="container-padding">
          <div className="max-w-2xl mx-auto text-center">
            <Star className="reveal w-8 h-8 text-white/80 mx-auto mb-6" />
            <h2 className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Discover the Beauty Within You</h2>
            <p className="reveal reveal-delay-2 text-white/80 mb-10">Take the first step on your personal development journey today.</p>
            <button onClick={() => openForm('personal_dev')} className="reveal reveal-delay-3 inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#B565CC] text-xs font-medium tracking-[0.15em] uppercase rounded-full hover:-translate-y-0.5 transition-all hover:shadow-lg arrow-hover" data-testid="personal-book-call">Book a Free Call <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PersonalPage;
